import { PayoutsAPI, serviceName } from '@frontend/api-payments';
import { useSchemaInfiniteQuery, useSchemaQuery } from '@frontend/react-query-helpers';
import { SchemaIO } from '@frontend/schema';

export const STANDARD_QUERY_ENDPOINT_NAMES = [
  'GetLocationPayout',
  'GetPayoutsSummary',
] as const satisfies (keyof typeof PayoutsAPI)[];
const INFINITE_QUERY_ENDPOINT_NAMES = ['ListPayouts'] as const satisfies (keyof Omit<
  typeof PayoutsAPI,
  (typeof STANDARD_QUERY_ENDPOINT_NAMES)[number]
>)[];
export type InfiniteQueryEndpointName = (typeof INFINITE_QUERY_ENDPOINT_NAMES)[number];
type EndpointName = 'ListPayouts';
type ServiceInfiniteQueries = Pick<typeof PayoutsAPI, EndpointName>;

export type UsePayoutsV1InfiniteQueryArgs<
  EndpointName extends InfiniteQueryEndpointName,
  E = unknown,
  D = SchemaIO<ServiceInfiniteQueries[EndpointName]>['output'],
  PageParam = any
> = Omit<
  Parameters<typeof useSchemaInfiniteQuery<ServiceInfiniteQueries, EndpointName, E, D, PageParam>>[0],
  'service' | 'serviceName'
>;

export type UsePayoutsV1InfiniteQueryEndpointArgs<
  EndpointName extends InfiniteQueryEndpointName,
  E = unknown,
  D = SchemaIO<ServiceInfiniteQueries[EndpointName]>['output'],
  PageParam = any,
  Request = SchemaIO<ServiceInfiniteQueries[EndpointName]>['input']
> = Omit<UsePayoutsV1InfiniteQueryArgs<EndpointName, E, D, PageParam>, 'endpointName' | 'request'> & {
  request: Request;
};

type ListPayoutsOutput = SchemaIO<ServiceInfiniteQueries['ListPayouts']>['output'];
type ListPayoutsBaseInput = SchemaIO<ServiceInfiniteQueries['ListPayouts']>['input'];
type PayoutsPageParam = string;
type UseListPayoutsInfiniteQueryArgs<E = unknown, TData = ListPayoutsOutput> = Omit<
  Parameters<
    typeof useSchemaInfiniteQuery<
      typeof PayoutsAPI, // The type of the bound service
      'ListPayouts', // The endpoint name (must be a key of SchemaPayoutsService)
      E, // Error type for the query
      TData, // Data type returned by the query (ListPayoutsResponse)
      PayoutsPageParam // The type of the page parameter (string for pageToken)
    >
  >[0],
  // Omit properties that will be pre-filled by this custom hook:
  'service' | 'serviceName' | 'endpointName' | 'request'
> & {
  requestParams: ListPayoutsBaseInput;
};

export const useListPayoutsV1InfiniteQuery = <E = unknown, TData extends ListPayoutsOutput = ListPayoutsOutput>(
  args: UseListPayoutsInfiniteQueryArgs<E, TData>
) => {
  const { requestParams, options, ...restArgs } = args;

  return useSchemaInfiniteQuery<typeof PayoutsAPI, 'ListPayouts', E, TData, PayoutsPageParam>({
    service: PayoutsAPI,
    serviceName,
    endpointName: 'ListPayouts',
    request: ({ pageParam }: { pageParam?: PayoutsPageParam }) => ({
      ...requestParams, // Includes locationIds, pageSize, filter, orderBy, etc.
      pageToken: pageParam, // `pageParam` is the `nextPageToken` from the previous response.
    }),
    // React Query options
    options: {
      ...options, // Allows users to pass through other React Query options.
      getNextPageParam: (lastPage): PayoutsPageParam | undefined => {
        // Extract `nextPageToken` from the last fetched page.
        // If it exists, it's used as the `pageParam` for the next request.
        // If undefined, React Query knows there are no more pages.
        return lastPage.nextPageToken || undefined;
      },
    },
    ...restArgs,
  });
};

export const useGetPayoutsSummaryV1Query = ({ locationIds }: { locationIds: string[] }) => {
  return useSchemaQuery({
    service: PayoutsAPI,
    serviceName,
    endpointName: 'GetPayoutsSummary',
    request: {
      locationIds,
    },
  });
};
